<template>
  <div class="tx-signing">
    <c-modal
      :persistent="true"
      :value="!processing && !txHash"
      @onClosed="$emit('cancel')"
    >
      <div v-if="!shouldShowImtokenNotice && !shouldShowMetamaskNotice">
        <div class="heading-4 font-weight-medium">{{ title }} Confirmation</div>
        <div
          class="text-5 darkgray--text smoke rounded-10 pa-3 mt-3"
          v-if="description"
        >
          {{ description }}
        </div>
        <div class="mt-5"></div>
        <div class="d-flex align-center mt-3" v-if="!hideProject">
          <div
            class="flex-2 text-left text-5 font-weight-medium text-uppercase gray--text"
          >
            Project:
          </div>
          <div class="flex-3 text-3">{{ project.text }}</div>
        </div>
        <slot></slot>
        <div class="d-flex align-center mt-3">
          <div
            class="flex-2 text-left text-5 font-weight-medium text-uppercase gray--text"
          >
            Transaction Fee:
          </div>
          <div class="flex-3 text-3">{{ txFees.amount | denomFormat }} ETH</div>
        </div>
        <br />
        <v-divider class="border-secondary"></v-divider>
        <br />
        <div class="text-6 darkgray--text smoke rounded-10 pa-5">
          Confirming will require you to sign this transaction and that you
          agree to the
          <a
            class="text-decoration-none"
            href="https://www.unagii.com/legal/terms"
            target="_blank"
            rel="noopener noreferrer"
            >Terms & Conditions</a
          >.
        </div>
        <br />
        <div class="d-flex justify-end align-center">
          <div
            class="text-4 font-weight-medium gray--text clickable"
            @click="$emit('cancel')"
          >
            Cancel
          </div>
          <c-btn @click="confirm" class="ml-5" :secondary="false"
            >Confirm</c-btn
          >
        </div>
      </div>
      <imtoken-notice
        v-if="shouldShowImtokenNotice"
        class="py-3"
        context="transaction"
        @next="$emit('cancel')"
      />
      <metamask-notice
        v-if="shouldShowMetamaskNotice"
        class="py-3"
        context="transaction"
        @next="$emit('cancel')"
      />
    </c-modal>
    <c-loading-dialog
      v-if="processing"
      title="Processing transaction"
      class="tx-signing-loading-dialog"
    />
    <tx-sent :isDydx="true" :txHash="txHash" v-if="txHash && !quiet" @next="onNext" />
  </div>
</template>
<script>
import { mapGetters } from "vuex";
import Config from "@/common/config";
import EthNetworks from "@/common/constants/ethNetworks";
import AppConst from "@/common/constants";
import TxSent from "@/components/tx/TxSent.vue";
import ImtokenNotice from "@/components/wallet/WalletNotice/ImToken";
import MetamaskNotice from "@/components/wallet/WalletNotice/Metamask";

const {
  fortmatic: fortmaticOptions,
  torus: TorusConfig,
  walletconnect,
} = Config;
const TX_TIMEOUT = 120000;

export default {
  name: "TxSigning",
  components: {
    TxSent,
    ImtokenNotice,
    MetamaskNotice,
  },
  props: {
    name: String,
    title: String,
    description: String,
    metadata: Object,
    walletAccount: Object,
    broadcastAction: String,
    broadcastMetadata: Object,
    quiet: {
      type: Boolean,
      default: false,
    },
    hideProject: {
      type: Boolean,
      default: false,
    },
    txObject: Object,
    txFees: Object,
  },
  data: () => ({
    processing: false,
    txHash: null,
    shouldShowImtokenNotice: false,
    shouldShowMetamaskNotice: false,
  }),
  computed: {
    ...mapGetters({
      activeBackend: "walletUtils/getActiveBackend",
      validBackend: "walletUtils/getValidBackend",
      signableWallet: "userWallets/signableWallet",
      isImToken: "appConfig/isImToken",
      isMetamask: "appConfig/isMetamask",
      project: "projectConfig/latestProject",
    }),
    accountSelected() {
      return this.walletAccount || this.signableWallet;
    },
    accountHdPath() {
      const { hdPath } = this.accountSelected.metadata || {};
      return hdPath || "";
    },
  },
  methods: {
    async connectWallet() {
      this.$store.dispatch("walletUtils/setBackend", this.accountSelected?.type);
      try {
        await this.$store.dispatch("walletUtils/importBackend", {
          fortmaticOptions,
          coinName: "ethereum",
          torusOptions: { network: TorusConfig.network },
          walletconnect: {
            projectId: walletconnect.projectId,
            network: "ethereum",
            accountAddress: this.accountSelected.address,
          },
        });
        if (this.validBackend && this.validBackend !== true) {
          throw new Error(this.validBackend);
        }
        const account = await this.activeBackend.getAccount({
          hdPath: this.accountHdPath,
        });
        const { address: accountAddress } = await account.getAddress();
        if (
          accountAddress.toLowerCase() !==
          this.accountSelected.address.toLowerCase()
        ) {
          throw new Error(
            `Please use the correct account to sign the transaction.\nExpected address: ${this.accountSelected.address}.\nActual address: ${accountAddress}.`
          );
        }
        return account;
      } catch (error) {
        if ("function" === typeof this.activeBackend?.cleanUp) {
          this.activeBackend.cleanUp();
        }
        this.onError(error);
      }
    },
    async confirm() {
      if (!this.accountSelected) {
        return this.onError(new Error('Signing wallet not found'));
      }
      if (this.accountSelected.type === "imtoken" && !this.isImToken) {
        return (this.shouldShowImtokenNotice = true);
      }
      if (this.accountSelected.type === "metamask" && !this.isMetamask) {
        return (this.shouldShowMetamaskNotice = true);
      }
      this.processing = true;
      let errorCode = AppConst.TX_ERROR.CONNECT_TIMEOUT;
      setTimeout(() => {
        if (this.processing) {
          this.onError(
            new Error(
              `Something went wrong! Please refresh the browser to continue.`
            )
          );
          this.processing = false;
        }
      }, TX_TIMEOUT);
      const account = await this.connectWallet();
      if (account) {
        try {
          errorCode = AppConst.TX_ERROR.SIGN_TIMEOUT;
          const { txHash, signedTx } = await account.signTransaction(
            this.txObject
          );
          errorCode = AppConst.TX_ERROR.BROADCAST_TIMEOUT;
          await this.broadcastTx({
            signedTx: signedTx?.slice(2) ?? undefined,
            txHash,
            type: this.name,
            metadata: this.metadata,
          });
          this.onSuccess(txHash, signedTx);
        } catch (error) {
          console.log('ERROR', error);
          this.onError(error);
        }
      }
    },
    async broadcastTx(payload) {
      if (!this.broadcastAction) return;
      return this.$store.dispatch(this.broadcastAction, {
        ...payload,
        ...(this.broadcastMetadata || {}),
      });
    },
    onError(error) {
      this.processing = false;
      this.$store.dispatch("addAlert", {
        type: "error",
        title: "Error",
        message: error.message,
      });
      this.cancelProcess();
    },
    cancelProcess() {
      this.$nextTick(() => {
        this.$emit("cancel");
      });
    },
    onSuccess(txHash, signedTx) {
      this.txHash = txHash;
      if (this.quiet) {
        this.onNext(txHash, signedTx);
      } else {
        this.processing = false;
      }
    },
    onNext(txHash, signedTx) {
      this.$emit("onSuccess", txHash, signedTx);
      if (!this.quiet) {
        this.$store.dispatch(`staking/${this.project.name}/setTxAction`);
      }
    },
  },
  async mounted() {
    if (this.accountSelected?.type === "metamask" && window.ethereum) {
      if (
        Config.ethNetworkChainId === "0x1" &&
        window.ethereum.chainId !== "0x1"
      ) {
        this.$store.dispatch("addAlert", {
          type: "error",
          title: "Error",
          message: `Please change the network to ${
            EthNetworks[Config.ethNetworkChainId]
          }.`,
        });
        this.cancelProcess();
        return;
      }
    }
  },
};
</script>
